import { useEffect, useState } from 'react';
import { axiosInstance } from 'utils/axios';

// material-ui
import { Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Box, Button, Grid, Typography, TextField, Select, MenuItem, Autocomplete, CircularProgress, Checkbox } from '@mui/material';

import { useTheme } from '@mui/material/styles';
import { deepPurple } from '@mui/material/colors';
import { gridSpacing } from 'app/constant';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCurrentToken } from 'features/auth/authSlice';
import { warningAlert, successAlert, errorAlert } from 'ui-component/Toasts';
import useFetchData from 'hooks/useFetchData';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from "react-i18next";
import ImagesDialog from './components/ImagesDialog';
import ApproveKYCModal from './components/ApproveKYCModal';
import RejectKYCModal from './components/RejectKYCModal';

const KYCManagement = () => {
    const { t } = useTranslation();
    const user = useSelector(selectCurrentUser);
    const token = useSelector(selectCurrentToken);
    const theme = useTheme();

    const [kycRecords, setKYCRecords] = useState([]);
    const [dateFrom, setDateFrom] = useState(dayjs().subtract(1, 'days'));
    const [dateTo, setDateTo] = useState(dayjs());
    const [customerLoginUserName, setCustomerLoginUserName] = useState('');
    const [numberCode, setNumberCode] = useState('');
    const [status, setstatus] = useState('1');
    const [filterBtnDisabled, setFilterBtnDisabled] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [fullName, setFullName] = useState('');
    const [idNumber, setIDNumber] = useState('');

    const fetchKYCRecords = async () => {
        let depositRecordURL = `/KYCVerification/All?createdDateFrom=${dateFrom.format('YYYY-MM-DD')}&createdDateTo=${dateTo.format('YYYY-MM-DD')}`;

        if (customerLoginUserName !== '') {
            depositRecordURL += `&loginUserName=${customerLoginUserName}`;
        }

        if (numberCode !== '') {
            depositRecordURL += `&numberCode=${numberCode}`;
        }

        if (status !== '') {
            depositRecordURL += `&statusID=${status}`;
        }

        if (fullName !== '') {
            depositRecordURL += `&fullName=${fullName}`;
        }

        if (idNumber !== '') {
            depositRecordURL += `&idNumber=${idNumber}`;
        }

        setLoading(true);
        setFilterBtnDisabled(true);
        try {
            const res = await axiosInstance.get(depositRecordURL, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (res.status === 200) {
                setKYCRecords(res.data);
            } else {
                errorAlert('Something went wrong.');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
            setFilterBtnDisabled(false);
        }
    };

    const { data: KYCStatus } = useFetchData('/KYCVerificationStatus/All');
    const { data: KYCRejectReasons } = useFetchData('/KYCVerificationRejectReasons/All');

    useEffect(() => {
        Promise.all([fetchKYCRecords()]).catch((error) => {
            console.error('An error occurred:', error);
        });
    }, []);

    function StickyHeadTable({ kyc, fetchKYCRecords, rejectReasons }) {
        const { t } = useTranslation();
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(100);
        const token = useSelector(selectCurrentToken);
        const [selectedCustomerRow, setSelectedCustomerRow] = useState(null);
        const [approveKYCModalOpen, setApproveKYCModalOpen] = useState(false);
        const [rejectKYCModalOpen, setRejectKYCModalOpen] = useState(false);

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(+event.target.value);
            setPage(0);
        };
        
        const openApproveKYCModal = (row) => {
            setSelectedCustomerRow(row);
            setApproveKYCModalOpen(true);
        };

        const openRejectKYCModal = (row) => {
            setSelectedCustomerRow(row);
            setRejectKYCModalOpen(true);
        };

        // const [isUserAllowEdit, setIsUserAllowEdit] = useState(false);
        // const fetchIsUserAllowEdit = async () => {
        //     try {
        //         const res = await axiosInstance.get('IsAllowEdit', {
        //             headers: {
        //                 Authorization: `Bearer ${token}`
        //             }
        //         });

        //         if (res.status === 200) {
        //             setIsUserAllowEdit(res.data);
        //             console.log('fetchIsUserAllowEdit', res.data);
        //         } else {
        //             errorAlert('Something went wrong.');
        //         }
        //     } catch (error) {
        //         console.log(error);
        //     }
        // };

        // useEffect(() => {
        //     Promise.all([fetchIsUserAllowEdit()]).catch((error) => {
        //         console.error('An error occurred:', error);
        //     });
        // }, []);

        return (
            <>
                <Paper sx={{ width: '100%', overflow: 'hidden', marginTop: '50px' }}>
                    <TableContainer sx={{ maxHeight: 440 }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    <TableCell key="id" style={{ minWidth: 170 }}>{t("withdrawal.date")}</TableCell>
                                    <TableCell key="customerDetails" style={{ minWidth: 170 }}>{t("withdrawal.customer")}</TableCell>
                                    <TableCell key="createdBy" style={{ minWidth: 170 }}>{t("withdrawal.admin")}</TableCell>
                                    <TableCell key="kycDetails" style={{ minWidth: 170 }}>{t("withdrawal.bankDetails")}</TableCell>
                                    <TableCell key="status" style={{ minWidth: 170 }}>{t("withdrawal.actualAmount")}</TableCell>
                                    <TableCell key="updatedBy" style={{ minWidth: 170 }}>{t("withdrawal.updatedBy")}</TableCell>
                                    <TableCell key="action" style={{ minWidth: 170 }}>{t("withdrawal.setting")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {kyc.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                                    return (
                                        <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                                            <TableCell key='id' style={{fontSize: "12px"}}>
                                                ID : {row.id} <br/>
                                                {t("withdrawal.createdAt")} {
                                                `${row.createdDate.split('T')[0]} ${new Date(
                                                    '1970-01-01T' + row.createdDate.split('T')[1] + 'Z'
                                                ).toLocaleTimeString('en-US', {
                                                    timeZone: 'UTC',
                                                    hour12: true,
                                                    hour: 'numeric',
                                                    minute: 'numeric',
                                                    second: 'numeric'
                                                })}`}
                                            </TableCell>
                                            <TableCell key='customerDetails' style={{fontSize: "12px"}}>
                                                {t("withdrawal.code")} : {row.customerNumberCode} <br/>
                                                {row.customerLoginUserName}
                                                {/* {t("withdrawal.phoneNumber")} : {row.customerPhoneNumber} <br/> */}
                                            </TableCell>
                                            <TableCell key='createdBy' style={{fontSize: "12px"}}>
                                                {/* {t("withdrawal.admin")}: {row.createdBy} <br/> */}
                                                {t("withdrawal.by")}: {row.referrerCustomerLoginUserName} {t("withdrawal.recommend")}
                                            </TableCell>
                                            <TableCell key='kycDetails' style={{fontSize: "12px"}}>
                                                {t("kyc.kycType")} : {row.typeName} <br/>
                                                {/* {t("kyc.kycFullName")} : {row.fullName} <br/>
                                                {t("kyc.kycIDNumber")} : {row.idNumber} <br/>
                                                {
                                                    row.duplicateFullName &&
                                                    <>
                                                        <span style={{color: 'red'}}>
                                                            {t("kyc.duplicateFullName")}
                                                        </span>
                                                    </>
                                                }
                                                {
                                                    row.duplicateIDNumber &&
                                                    <>
                                                        <span style={{color: 'red'}}>
                                                            {t("kyc.duplicateIDNumber")}
                                                        </span>
                                                    </>
                                                } */}
                                            </TableCell>
                                            <TableCell key="status" style={{ fontSize: "12px", verticalAlign: 'middle' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box>
                                                    <span style={{ fontWeight: 'bolder', color: 
                                                        row.statusID === 1 ? 'orange' : 
                                                        row.statusID === 2 ? 'green' : 
                                                        row.statusID === 3 ? 'red' : '' 
                                                    }}>
                                                        {row.statusID === 1
                                                        ? t("withdrawal.pending")
                                                        : row.statusID === 2
                                                        ? t("withdrawal.approved")
                                                        : row.statusID === 3
                                                        ? t("withdrawal.rejected")
                                                        : ''}
                                                    </span> <br/>
                                                    {
                                                        row.rejectReasonName && 
                                                        <>
                                                            {t("customer.rejectReason")}: {row.rejectReasonName}
                                                        </>
                                                    }
                                                </Box>

                                                <ImagesDialog imageUrl={row.imageUrl} />
                                            </Box>
                                            </TableCell>
                                            <TableCell key='updatedBy' style={{fontSize: "12px"}}>
                                                {row.updatedBy}
                                            </TableCell>
                                            <TableCell key='action' style={{fontSize: "12px"}}>
                                                <Button
                                                    onClick={() => openApproveKYCModal(row)}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: 'green',
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("withdrawal.approve")}
                                                </Button>
                                                <br/><br/>
                                                <Button
                                                    onClick={() => openRejectKYCModal(row)}
                                                    variant="contained"
                                                    style={{
                                                        border: 'none',
                                                        backgroundColor: 'red',
                                                        width: 'fit-content',
                                                        height: 'fit-content',
                                                        color: 'white',
                                                        padding: '6px 8px',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    {t("withdrawal.reject")}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={kyc.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>

                {selectedCustomerRow && (
                    <ApproveKYCModal
                        approveKYCModalOpen={approveKYCModalOpen}
                        setApproveKYCModalOpen={setApproveKYCModalOpen}
                        row={selectedCustomerRow}
                        fetchKYCRecords={fetchKYCRecords}
                    />
                )}
            
                {selectedCustomerRow && (
                    <RejectKYCModal
                        rejectKYCModalOpen={rejectKYCModalOpen}
                        setRejectKYCModalOpen={setRejectKYCModalOpen}
                        row={selectedCustomerRow}
                        fetchKYCRecords={fetchKYCRecords}
                        rejectReasons={rejectReasons}
                    />
                )}
            </>
        );
    }

    return (
        <Grid container item xs={12} padding="20px">
            <Grid item container xs={12} spacing={gridSpacing} sx={{ marginBottom: '10px', overflowY: 'auto' }}>
                <Grid item container xs={12} md={6} lg={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            <span style={{ color: 'red' }}>*</span>{t("withdrawal.createdDate")} :
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={dateFrom}
                                slotProps={{
                                    textField: {
                                        error: false,
                                        size: 'small', // makes the input smaller (MUI supports 'small' and 'medium')
                                        sx: {
                                          '& .MuiInputBase-root': {
                                            fontSize: '13px',      // font size of the input
                                            padding: '4px 8px',    // optional: reduce input padding
                                            minHeight: '36px',     // optional: control input height
                                          },
                                          '& .MuiInputBase-input': {
                                            fontSize: '13px',      // font size inside input
                                            padding: '8.5px 14px', // adjust input padding if needed
                                          },
                                          '& .MuiInputLabel-root': {
                                            fontSize: '13px',      // label font size
                                          },
                                        },
                                    }
                                }}
                                onChange={(newValue) => setDateFrom(newValue)}
                            />
                        </LocalizationProvider>
                        -
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={dateTo}
                                slotProps={{
                                    textField: {
                                        error: false,
                                        size: 'small', // makes the input smaller (MUI supports 'small' and 'medium')
                                        sx: {
                                          '& .MuiInputBase-root': {
                                            fontSize: '13px',      // font size of the input
                                            padding: '4px 8px',    // optional: reduce input padding
                                            minHeight: '36px',     // optional: control input height
                                          },
                                          '& .MuiInputBase-input': {
                                            fontSize: '13px',      // font size inside input
                                            padding: '8.5px 14px', // adjust input padding if needed
                                          },
                                          '& .MuiInputLabel-root': {
                                            fontSize: '13px',      // label font size
                                          },
                                        },
                                    }
                                }}
                                onChange={(newValue) => setDateTo(newValue)}
                            />
                        </LocalizationProvider>
                    </Box>
                </Grid>
                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("withdrawal.loginUserName")} :
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            value={customerLoginUserName}
                            onChange={(e) => setCustomerLoginUserName(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '24px',
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '24px',
                                    padding: '4px 6px',
                                },
                                '& input': {
                                    padding: '4px 6px',
                                },
                            }}
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("withdrawal.code")} :
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            value={numberCode}
                            onChange={(e) => setNumberCode(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '24px',
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '24px',
                                    padding: '4px 6px',
                                },
                                '& input': {
                                    padding: '4px 6px',
                                },
                            }}
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("kyc.kycStatus")} :
                        </Typography>
                        <Select
                            displayEmpty
                            value={status}
                            onChange={(e) => setstatus(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '36px',
                                '& .MuiSelect-select': {
                                    fontSize: '13px',
                                    padding: '10px 14px',
                                },
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '36px',
                                    padding: '10px 14px',
                                },
                            }}
                        >
                            <MenuItem sx={{ width: '100%' }} value="">
                                {t("kyc.pleaseSelectKYCStatus")}
                            </MenuItem>
                            {KYCStatus.map((item, index) => (
                                <MenuItem sx={{ width: '100%' }} value={item.id} key={index}>
                                    {item.id === 1 ? t("withdrawal.pending") : item.id === 2 ? t("withdrawal.approved") : item.id === 3 ? t("withdrawal.rejected") : ''}
                                    {/* {item.name} */}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("kyc.kycFullName")} :
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '24px',
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '24px',
                                    padding: '4px 6px',
                                },
                                '& input': {
                                    padding: '4px 6px',
                                },
                            }}
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={6} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {t("kyc.kycIDNumber")} :
                        </Typography>
                        <TextField
                            id="outlined-basic"
                            variant="outlined"
                            value={idNumber}
                            onChange={(e) => setIDNumber(e.target.value)}
                            sx={{
                                fontSize: '13px',
                                minHeight: '24px',
                                '& .MuiInputBase-root': {
                                    fontSize: '13px',
                                    minHeight: '24px',
                                    padding: '4px 6px',
                                },
                                '& input': {
                                    padding: '4px 6px',
                                },
                            }}
                        />
                    </Box>
                </Grid>

                <Grid item container xs={12} md={12} lg={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'end',
                            alignItems: 'center',
                            gap: '15px',
                            width: '50%',
                            marginLeft: 'auto'
                        }}
                    >
                        <Button
                            variant="contained"
                            sx={{
                                float: 'right',
                                display: 'block',
                                mx: 'auto',
                                width: '150px',
                                backgroundColor: deepPurple[500],
                                marginTop: '20px',
                                marginBottom: '30px'
                            }}
                            onClick={() => fetchKYCRecords()}
                            disabled={filterBtnDisabled}
                        >
                            {t("withdrawal.filter")}
                        </Button>
                    </Box>
                </Grid>
                <Box sx={{ width: '100%', minHeight: '200px' }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <StickyHeadTable
                            kyc={kycRecords}
                            fetchKYCRecords={fetchKYCRecords}
                            rejectReasons={KYCRejectReasons}
                        />
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

export default KYCManagement;
